import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, CircularProgress, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { Close, DownloadOutlined, InsertDriveFileOutlined, UploadFileOutlined } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service'
import { ICapitalGood, IProductDocument } from '../../../../shared/services/capital-good/capital-good.model'
import { IEpd } from '../../../../shared/services/capital-good/epd.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'
import { DialogWrapper } from '../../display-elements-components/dialog-wrapper'
import { downloadBlob } from '../../../../shared/utils/download-file-utils'
import { PRODUCT_DOCUMENT_TYPE_LABELS, PRODUCT_DOCUMENT_TYPE_OPTIONS } from '../../../../shared/constants/product-repository'

const ACCEPTED_FILE_TYPES = 'application/pdf,image/png,image/jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const emptyForm = {
  documentType: 'lca_report',
  issuer: '',
  effectiveDate: '',
  expiryDate: '',
}

function formatSize(size: number | null): string {
  if (!size) return ''
  return `${Math.max(1, Math.round(size / 1024))} KB`
}

function formatValidity(effectiveDate: string | null, expiryDate: string | null): string {
  const parts: string[] = []
  if (effectiveDate) parts.push(`Effective ${moment(effectiveDate).format('DD/MM/YYYY')}`)
  if (expiryDate) parts.push(`Expires ${moment(expiryDate).format('DD/MM/YYYY')}`)
  return parts.join(' · ')
}

/**
 * The typed document registry for one product: EPDs (managed through the structured EPD flow,
 * because an EPD can drive the factor) alongside LCA reports, certificates and test reports,
 * each held with its type, issuer and validity dates.
 */
export const ProductDocumentsPanel: FunctionComponent<{
  good: ICapitalGood,
  canEdit: boolean,
  onManageEpd: () => void,
  epdRefreshKey?: number,
}> = ({ good, canEdit, onManageEpd, epdRefreshKey = 0 }) => {
  const capitalGoodService = new CapitalGoodService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const [documents, setDocuments] = useState<IProductDocument[]>([])
  const [epds, setEpds] = useState<IEpd[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchAll = useCallback((isActive: () => boolean = () => true) => {
    Promise.all([
      capitalGoodService.getDocuments(good.id),
      capitalGoodService.getEpds(good.id),
    ])
      .then(([documentRows, epdRows]) => {
        if (!isActive()) return
        setDocuments(documentRows)
        setEpds(epdRows)
        setErrorMessage('')
      })
      .catch((err) => {
        console.error(err)
        if (isActive()) setErrorMessage('Unable to load the product documents. Please try again.')
      })
      .finally(() => {
        if (isActive()) setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [good.id])

  useEffect(() => {
    let active = true
    setIsLoading(true)
    fetchAll(() => active)
    return () => { active = false }
  }, [fetchAll, epdRefreshKey])

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const closeAddDialog = () => {
    setAddDialogOpen(false)
    setFile(null)
    setForm({ ...emptyForm })
  }

  const submitHandler = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('files', file)
      formData.append('productDocument', JSON.stringify({
        documentType: form.documentType,
        issuer: form.issuer.trim() || null,
        effectiveDate: form.effectiveDate || null,
        expiryDate: form.expiryDate || null,
      }))
      await capitalGoodService.uploadDocument(good.id, formData)
      userNotificationsContext.openSnackbar({ open: true, message: 'Document added' })
      closeAddDialog()
      fetchAll()
    } catch (err: any) {
      console.error(err)
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The document could not be uploaded. Please try again.',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const removeHandler = (productDocument: IProductDocument) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you want to remove "${productDocument.document?.filename || 'this document'}"? This action cannot be undone.`,
      buttonCopy: 'Remove document',
      cancelButtonCopy: 'Cancel',
    }).then(async (confirmed) => {
      if (!confirmed) return
      try {
        await capitalGoodService.deleteDocument(good.id, productDocument.id)
        userNotificationsContext.openSnackbar({ open: true, message: 'Document removed' })
        fetchAll()
      } catch (err: any) {
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'The document could not be removed. Please try again.',
          backgroundColor: 'text.primary',
        })
      }
    })
  }

  const downloadDocumentHandler = async (productDocument: IProductDocument) => {
    try {
      const blob = await capitalGoodService.downloadDocument(good.id, productDocument.id)
      downloadBlob({ blob, fileName: productDocument.document?.filename || 'document' })
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not download the document', backgroundColor: 'text.primary' })
    }
  }

  const downloadEpdHandler = async (epd: IEpd) => {
    try {
      const blob = await capitalGoodService.downloadEpd(good.id, epd.id)
      downloadBlob({ blob, fileName: epd.document?.filename || 'epd.pdf' })
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not download the EPD', backgroundColor: 'text.primary' })
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
        <LoadingSpinner />
      </Box>
    )
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{errorMessage}</Typography>
      </Box>
    )
  }

  const hasAnything = documents.length > 0 || epds.length > 0

  return (
    <Stack spacing={2}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        The documents held against this product: EPDs, LCA reports, certificates and test reports,
        each with its issuer and validity dates. They appear in the product's footprint pack.
      </Typography>
      {!hasAnything && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          No documents are attached to this product yet.
        </Typography>
      )}
      {epds.length > 0 && (
        <Stack spacing={1}>
          {epds.map((epd) => (
            <Box key={epd.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' noWrap>{epd.document?.filename || epd.registrationNumber || 'EPD'}</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {[epd.programOperator, formatValidity(epd.issueDate, epd.validUntil)].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Chip size='small' variant='outlined' label='EPD' />
              {epd.appliedToGood && <Chip size='small' color='primary' label='Provides the factor' />}
              <IconButton size='small' onClick={() => downloadEpdHandler(epd)}>
                <DownloadOutlined fontSize='small' />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
      {documents.length > 0 && (
        <Stack spacing={1}>
          {documents.map((productDocument) => (
            <Box key={productDocument.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' noWrap>{productDocument.document?.filename || 'Document'}</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {[
                    productDocument.issuer,
                    formatValidity(productDocument.effectiveDate, productDocument.expiryDate),
                    formatSize(productDocument.document?.size ?? null),
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Chip size='small' variant='outlined' label={PRODUCT_DOCUMENT_TYPE_LABELS[productDocument.documentType] || productDocument.documentType} />
              <IconButton size='small' onClick={() => downloadDocumentHandler(productDocument)}>
                <DownloadOutlined fontSize='small' />
              </IconButton>
              {canEdit && (
                <IconButton size='small' onClick={() => removeHandler(productDocument)}>
                  <Close fontSize='small' />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}
      {canEdit && (
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<UploadFileOutlined fontSize='small' />}
            onClick={() => setAddDialogOpen(true)}
          >
            <Typography sx={{ pl: 0.5 }}>Add document</Typography>
          </Button>
          <Button variant='outlined' onClick={onManageEpd}>
            <Typography sx={{ pl: 0.5 }}>Manage EPDs</Typography>
          </Button>
        </Stack>
      )}

      <DialogWrapper
        open={addDialogOpen}
        onClose={closeAddDialog}
        title='Add a product document'
        cancelHandler={closeAddDialog}
        submitHandler={submitHandler}
        isSubmitting={isSubmitting}
        disableSaveButton={!file}
        maxWidth='sm'
      >
        <Stack spacing={2}>
          <Button component='label' variant='outlined' startIcon={isSubmitting ? <CircularProgress size={16} /> : <UploadFileOutlined />}>
            {file ? file.name : 'Choose file'}
            <input
              type='file'
              hidden
              accept={ACCEPTED_FILE_TYPES}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
                event.target.value = ''
              }}
            />
          </Button>
          <TextField select size='small' label='Document type' value={form.documentType} onChange={(event) => setField('documentType', event.target.value)}>
            {PRODUCT_DOCUMENT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField size='small' label='Issuer (optional)' value={form.issuer} onChange={(event) => setField('issuer', event.target.value)} />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack direction='row' spacing={2}>
              <DatePicker
                label='Effective date'
                format='DD/MM/YYYY'
                value={form.effectiveDate ? moment(form.effectiveDate) : null}
                onChange={(date) => setField('effectiveDate', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
              />
              <DatePicker
                label='Expiry date'
                format='DD/MM/YYYY'
                value={form.expiryDate ? moment(form.expiryDate) : null}
                onChange={(date) => setField('expiryDate', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
              />
            </Stack>
          </LocalizationProvider>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            To attach an EPD, use Manage EPDs instead: an EPD's verified figure can become the product's emission factor.
          </Typography>
        </Stack>
      </DialogWrapper>
    </Stack>
  )
}
